import { useTranslation } from 'react-i18next';
import './SubCategories.css';
import { Link, useParams } from 'react-router-dom';
import { fetchCategories, fetchSubcategories } from "../../redux/actions/CategoriesAction";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from '../../App';

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5
        }
    }
};

function SubCategoriesPage() {
    const { t, i18n } = useTranslation("global");
    const { id } = useParams();
    const dispatch = useDispatch();
    const { isDarkMode } = useDarkMode();

    const {
        categories,
        subcategories,
        isLoading,
        error
    } = useSelector((state) => state.categories);

    const selectedCategory = useMemo(() =>
        categories.find(category => category.id === parseInt(id)),
        [categories, id]
    );
    useEffect(() => {
        window.scrollTo(0, 0);
        dispatch(fetchCategories(i18n.language));
    }, [dispatch, i18n.language]);

    useEffect(() => {
        // //console.log('=== USEEFFECT TRIGGERED ===');
        // //console.log('ID:', id, 'Type:', typeof id);
        // //console.log('Language:', i18n.language);
        // //console.log('Current subcategories state:', subcategories);
        
        if (id) {
            // //console.log('🚀 Dispatching fetchSubcategories for ID:', id);
            dispatch(fetchSubcategories({
                categoryId: id,
                lang: i18n.language
            }));
        } else {
            // //console.log('❌ No ID provided, skipping fetch');
        }
    }, [dispatch, id, i18n.language]);

    // Memoize current subcategories to prevent unnecessary re-renders
    const currentSubcategories = useMemo(() => {
        // //console.log('=== SUB CATEGORIES DEBUG ===');
        // //console.log('Subcategories data:', subcategories);
        // //console.log('Category ID:', id, 'Type:', typeof id);
        // //console.log('Subcategories for ID:', subcategories[id]);
        
        const arr = subcategories[id];
        // //console.log('Array check:', Array.isArray(arr));
        // //console.log('Array length:', arr?.length);
        // //console.log('Array content:', arr);
        
        if (Array.isArray(arr)) {
            // //console.log('✅ Returning array directly:', arr);
            return arr;
        }
        if (arr && Array.isArray(arr.data)) {
            // //console.log('✅ Returning array.data:', arr.data);
            return arr.data;
        }
        // //console.log('❌ Returning empty array - no data found');
        return [];
    }, [subcategories, id]);

    // //console.log('=== RENDER CHECK ===');
    // //console.log('isLoading:', isLoading);
    // //console.log('error:', error);
    // //console.log('currentSubcategories:', currentSubcategories);
    // //console.log('currentSubcategories length:', currentSubcategories?.length);

    if (isLoading) {
        // //console.log('Showing loading state');
        return <div className="skeleton-banner">
            <div className="skeleton skeleton-title"></div>
            <div className="skeleton skeleton-image"></div>
        </div>;
    }
    
    if (error) {
        // //console.log('Showing error state:', error);
        return <div className="error">Error: {error}</div>;
    }

    return (
        <>
            <motion.div 
                className={`breadcrumb-box text-center py-5 mb-4 rounded-4 shadow-sm ${isDarkMode ? 'dark-mode' : ''}`}
                style={{ 
                    background: isDarkMode 
                        ? "linear-gradient(135deg, #1a1b3a 0%, #0a0e27 100%)"
                        : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                    border: isDarkMode 
                        ? "1px solid #2d3748"
                        : "1px solid rgba(0,12,61,0.08)",
                    backdropFilter: "blur(10px)"
                }}
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
            >
                <motion.h4 
                    className="fw-bold mb-3" 
                    style={{ 
                        color: isDarkMode ? 'var(--color-text)' : '#000c3d', 
                        fontSize: '2.5rem',
                        background: isDarkMode ? "var(--color-text)" : "#000c3d",
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                    }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    {t('subcategory.title')}
                </motion.h4>
                <motion.p 
                    className="text-muted"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <Link 
                        to="/" 
                        className="text-decoration-none hover-text-primary"
                        style={{
                            transition: 'color 0.3s ease',
                            fontSize: '1.1rem',
                            color: isDarkMode ? 'var(--color-muted)' : '#6c757d'
                        }}
                    >
                        {t('subcategory.home')}
                    </Link>
                    <span className="mx-2" style={{ color: isDarkMode ? 'var(--color-muted)' : '#6c757d' }}>/</span>
                    <span className="fw-bold" style={{ 
                        color: isDarkMode ? 'var(--color-text)' : '#000c3d',
                        fontSize: '1.1rem'
                    }}>
                        {selectedCategory?.name || t("subcategory.category")} / {t('subcategory.title')}
                    </span>
                </motion.p>
            </motion.div>
            <SubCategoriesContent
                subcategories={currentSubcategories}
                categoryId={id}
            />
        </>
    );
}

// Pure component for better performance
const SubCategoriesContent = ({ subcategories, categoryId }) => {
    const { t, i18n } = useTranslation("global");
    const dispatch = useDispatch();
    const { pagination } = useSelector((state) => state.categories);
    const { isDarkMode } = useDarkMode();

    const currentPagination = pagination[categoryId] || {};
    const { current_page, total_pages } = currentPagination;
    
    // Use the subcategories prop instead of currentSubcategories
    const currentSubcategories = subcategories;
    
    // //console.log('=== SUB CATEGORIES CONTENT ===');
    // //console.log('Received subcategories prop:', subcategories);
    // //console.log('Current subcategories:', currentSubcategories);
    // //console.log('Is array:', Array.isArray(currentSubcategories));
    // //console.log('Length:', currentSubcategories?.length);

    const handlePageChange = (page) => {
        dispatch(fetchSubcategories({ categoryId, lang: i18n.language, page }));
    };

    // //console.log('=== SUB CATEGORIES CONTENT RENDER ===');
    // //console.log('About to render with subcategories:', currentSubcategories);
    // //console.log('Length:', currentSubcategories?.length);
    // //console.log('Will show data?', currentSubcategories?.length > 0);

    return (
        <section className={`subcategories-section py-5 ${isDarkMode ? 'dark-mode' : ''}`} style={{ 
            minHeight: '70vh',
            position: 'relative',
            background: isDarkMode ? '#0a0e27' : 'transparent'
        }}>
            <div className="container">
                <div className="row">
                        <div className="col-12">
                            {(() => {
                                // //console.log('Rendering check - currentSubcategories:', currentSubcategories);
                                // //console.log('Rendering check - length:', currentSubcategories?.length);
                                return null;
                            })()}
                            {currentSubcategories?.length > 0 ? (
                            <motion.div 
                                className="row g-4"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                {currentSubcategories?.map((subcategory, index) => {
                                    // //console.log(`Rendering subcategory ${index}:`, subcategory);
                                    
                                    return (
                                    <motion.div 
                                        className="col-lg-4 col-md-6 col-sm-12" 
                                        key={subcategory.id}
                                        // variants={cardVariants}
                                        // whileHover={{ 
                                        //     y: -10, 
                                        //     scale: 1.02,
                                        //     transition: { duration: 0.3 } 
                                        // }}
                                    >
                                        <Link 
                                            to={`/departments/${categoryId}/${subcategory.id}`} 
                                            className="text-decoration-none"
                                        >
                                            <motion.div 
                                                className={`subcategory-card h-100 ${isDarkMode ? 'dark-mode' : ''}`}
                                                style={{
                                                    background: isDarkMode 
                                                        ? 'linear-gradient(145deg, var(--color-card) 0%, #1a1b3a 100%)'
                                                        : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                                                    borderRadius: '30px',
                                                    overflow: 'hidden',
                                                    boxShadow: isDarkMode 
                                                        ? '0 25px 50px rgba(0,0,0,0.3), 0 10px 20px rgba(0,0,0,0.2)'
                                                        : '0 25px 50px rgba(0,0,0,0.1), 0 10px 20px rgba(0,0,0,0.05)',
                                                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    cursor: 'pointer',
                                                    border: isDarkMode 
                                                        ? '1px solid var(--color-border)'
                                                        : '1px solid rgba(255,255,255,0.8)',
                                                    position: 'relative',
                                                    transform: 'perspective(1000px) rotateX(0deg)'
                                                }}
                                            >
                                                {/* Image Section */}
                                                <div className="subcategory-image position-relative" style={{ height: "300px", overflow: 'hidden' }}>
                                                    <img
                                                        src={subcategory.image || "/Frame.png"}
                                                        alt={subcategory.name}
                                                        className="w-100 h-100 object-fit-cover"
                                                        style={{ transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
                                                        onError={(e) => {
                                                            e.target.src = "/Frame.png";
                                                        }}
                                                    />
                                                    
                                                    {/* Gradient Overlay */}
                                                    <div 
                                                        className="position-absolute top-0 start-0 w-100 h-100"
                                                        style={{
                                                            background: 'linear-gradient(135deg, rgba(0,12,61,0.6) 0%, rgba(26,35,126,0.4) 50%, rgba(102,126,234,0.3) 100%)',
                                                            backdropFilter: 'blur(2px)'
                                                        }}
                                                    ></div>

                                                    {/* Hover Overlay */}
                                                    <div 
                                                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                        style={{
                                                            background: 'linear-gradient(135deg, rgba(0,12,61,0.95) 0%, rgba(26,35,126,0.9) 50%, rgba(102,126,234,0.85) 100%)',
                                                            opacity: 0,
                                                            transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            backdropFilter: 'blur(5px)'
                                                        }}
                                                    >
                                                        <div className="text-center text-white">
                                                            <motion.div
                                                                initial={{ scale: 0.8, opacity: 0 }}
                                                                whileHover={{ scale: 1, opacity: 1 }}
                                                                transition={{ duration: 0.3 }}
                                                            >
                                                                <i className="fas fa-eye mb-3" style={{ fontSize: '2.5rem' }}></i>
                                                                <p className="mb-0 fw-bold" style={{ fontSize: '1.2rem' }}>{t('subcategory.viewSubcategory')}</p>
                                                            </motion.div>
                                                        </div>
                                                    </div>

                                                    {/* Decorative Elements */}
                                                    <div className="position-absolute top-0 end-0 m-4">
                                                        <motion.div 
                                                            className="rounded-circle d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: '45px',
                                                                height: '45px',
                                                                background: 'rgba(255,255,255,0.3)',
                                                                backdropFilter: 'blur(20px)',
                                                                border: '2px solid rgba(255,255,255,0.4)',
                                                                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                            whileHover={{ 
                                                                scale: 1.1, 
                                                                rotate: 360,
                                                                transition: { duration: 0.5 }
                                                            }}
                                                        >
                                                            <i className="fas fa-arrow-left text-white" style={{ fontSize: '0.9rem' }}></i>
                                                        </motion.div>
                                                    </div>
                                                </div>

                                                {/* Content Section */}
                                                <div className="subcategory-content p-5 text-center" style={{
                                                    background: isDarkMode 
                                                        ? 'linear-gradient(145deg, var(--color-card) 0%, #1a1b3a 100%)'
                                                        : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                                                    position: 'relative'
                                                }}>
                                                    {/* Content Background Pattern */}
                                                    <div 
                                                        className="position-absolute top-0 start-0 w-100 h-100"
                                                        style={{
                                                            background: 'radial-gradient(circle at 50% 0%, rgba(102, 126, 234, 0.05) 0%, transparent 50%)',
                                                            zIndex: 0
                                                        }}
                                                    ></div>
                                                    
                                                    <h5 
                                                        className="fw-bold mb-4 position-relative" 
                                                        style={{ 
                                                            color: isDarkMode ? 'var(--color-text)' : '#000c3d', 
                                                            fontSize: '1rem',
                                                            lineHeight: '1.6',
                                                            minHeight: '4.5rem',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            textShadow: isDarkMode ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                                                            zIndex: 1
                                                        }}
                                                    >
                                                        {subcategory.name}
                                                    </h5>
                                                    
                                                    <div className="d-flex align-items-center justify-content-center position-relative" style={{ zIndex: 1 }}>
                                                        <motion.button 
                                                            className="btn btn-primary rounded-pill px-6 py-3"
                                                            style={{
                                                                background: isDarkMode ? 'var(--color-primary)' : "#000c3d",
                                                                border: 'none',
                                                                fontSize: '0.9rem',
                                                                fontWeight: '600',
                                                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                boxShadow: 'none',
                                                                position: 'relative',
                                                                overflow: 'hidden',
                                                                color: 'white'
                                                            }}
                                                            whileHover={{ 
                                                                scale: 1.05,
                                                                transition: { duration: 0.3 }
                                                            }}
                                                            whileTap={{ scale: 0.95 }}
                                                        >
                                                            <i className="fas fa-shopping-bag mx-2" style={{ fontSize: '1.2rem' }}></i>
                                                        {t('subcategory.showProducts')}
                                                        </motion.button>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        </Link>
                                    </motion.div>
                                    );
                                })}
                            </motion.div>
                        ) : (
                            <motion.div 
                                className="col-12 text-center py-5"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6 }}
                            >
                                {(() => {
                                    // //console.log('No subcategories found - showing empty state');
                                    // //console.log('Current subcategories:', currentSubcategories);
                                    return null;
                                })()}
                                <div 
                                    className={`alert alert-info rounded-4 shadow-sm ${isDarkMode ? 'dark-mode' : ''}`}
                                    role="alert" 
                                    style={{ 
                                        fontSize: '1.2rem',
                                        background: isDarkMode 
                                            ? 'linear-gradient(135deg, var(--color-card) 0%, #1a1b3a 100%)'
                                            : 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                                        border: isDarkMode 
                                            ? '1px solid var(--color-border)'
                                            : '1px solid rgba(33,150,243,0.2)',
                                        color: isDarkMode ? 'var(--color-text)' : '#1565c0',
                                        padding: '30px',
                                        borderRadius: '20px'
                                    }}
                                >
                                    <i className="fas fa-info-circle me-3" style={{ fontSize: '1.5rem' }}></i>
                                    {t('subcategory.noSubcategories')}
                                </div>
                            </motion.div>
                            )}
                        </div>
                </div>

                {/* Enhanced Pagination */}
                {total_pages > 1 && (
                    <motion.div 
                        className={`d-flex justify-content-center mt-5 ${isDarkMode ? 'dark-mode' : ''}`}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        style={{
                            background: isDarkMode 
                                ? 'rgba(26, 27, 58, 0.9)'
                                : 'rgba(255,255,255,0.8)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '50px',
                            padding: '20px',
                            border: isDarkMode 
                                ? '1px solid var(--color-border)'
                                : '1px solid rgba(0,12,61,0.1)',
                            boxShadow: isDarkMode 
                                ? '0 10px 30px rgba(0,0,0,0.3)'
                                : '0 10px 30px rgba(0,0,0,0.1)'
                        }}
                    >
                        <nav aria-label="Subcategories pagination">
                            <ul className="pagination pagination-lg">
                                {/* Previous Button */}
                                <li className={`page-item ${current_page === 1 ? 'disabled' : ''}`}>
                                <button
                                        className="page-link rounded-pill me-2" 
                                    onClick={() => handlePageChange(current_page - 1)}
                                        disabled={current_page === 1}
                                        style={{ 
                                            background: current_page === 1 
                                                ? (isDarkMode ? 'var(--color-card)' : '#f8f9fa')
                                                : (isDarkMode ? 'white' : '#000c3d'),
                                            color: current_page === 1 
                                                ? (isDarkMode ? 'var(--color-muted)' : '#6c757d')
                                                : (isDarkMode ? '#000c3d' : 'white'),
                                            border: '2px solid #a0aec0',
                                            padding: '12px 20px',
                                            fontWeight: '600',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        <i className="fas fa-chevron-left me-1"></i>
                                        {t('common.previous')}
                                </button>
                                </li>
                                
                                {/* Page Numbers */}
                                {Array.from({ length: total_pages }, (_, i) => i + 1).map(page => (
                                    <li key={page} className="page-item">
                                <button
                                            className="page-link rounded-circle mx-1" 
                                            onClick={() => handlePageChange(page)}
                                            style={{ 
                                                background: current_page === page 
                                                    ? (isDarkMode ? 'white' : '#000c3d')
                                                    : 'transparent',
                                                color: current_page === page 
                                                    ? (isDarkMode ? '#000c3d' : 'white')
                                                    : (isDarkMode ? 'white' : '#000c3d'),
                                                border: '2px solid #a0aec0',
                                                width: '50px',
                                                height: '50px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: '600',
                                                fontSize: '1.1rem',
                                                transition: 'all 0.3s ease',
                                                transform: current_page === page ? 'scale(1.1)' : 'scale(1)',
                                                boxShadow: 'none'
                                            }}
                                        >
                                            {page}
                                </button>
                                    </li>
                            ))}
                                
                                {/* Next Button */}
                                <li className={`page-item ${current_page === total_pages ? 'disabled' : ''}`}>
                                <button
                                        className="page-link rounded-pill ms-2" 
                                    onClick={() => handlePageChange(current_page + 1)}
                                        disabled={current_page === total_pages}
                                        style={{ 
                                            background: current_page === total_pages 
                                                ? (isDarkMode ? 'var(--color-card)' : '#f8f9fa')
                                                : (isDarkMode ? 'white' : '#000c3d'),
                                            color: current_page === total_pages 
                                                ? (isDarkMode ? 'var(--color-muted)' : '#6c757d')
                                                : (isDarkMode ? '#000c3d' : 'white'),
                                            border: '2px solid #a0aec0',
                                            padding: '12px 20px',
                                            fontWeight: '600',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        {t('common.next')}
                                        <i className="fas fa-chevron-right ms-1"></i>
                                </button>
                                </li>
                            </ul>
                        </nav>
                    </motion.div>
                            )}
            </div>
        </section>
    );
};

export default SubCategoriesPage;